import { Component } from '@theme/component';

/**
 * Tabbed comparison for N products, with a sliding pill that mirrors the active tab.
 *
 * Sibling of assets/comparison-toggle.js rather than a generalisation of it: that component is
 * hardcoded to exactly two panels ('custom' / 'premade') and is what the live homepage and
 * /collections/custom-stickers render, so it is left at zero diff. Same house convention as
 * blocks/sheet-*.liquid being separate files from their design-*.liquid twins.
 *
 * Addresses panels by INDEX rather than by name, so the panel count is whatever the section's
 * blocks say it is.
 *
 * @typedef {object} ComparisonTabsRefs
 * @property {HTMLButtonElement[]} tab
 * @property {HTMLElement[]} panel
 * @property {HTMLElement} pill
 * @property {HTMLElement} [tablist]
 *
 * @extends {Component<ComparisonTabsRefs>}
 */
export class ComparisonTabsComponent extends Component {
  requiredRefs = ['tab', 'panel', 'pill'];

  #activeIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#activate(this.#activeIndex);

    // Theme editor only: selecting a Tab block in the sidebar should bring its panel forward.
    // Shopify re-renders the section on every edit, which constructs a fresh element with
    // #activeIndex back at 0 — so without this a merchant editing the fourth tab keeps being
    // shown the first one. The tab buttons carry block.shopify_attributes, so the event's target
    // is the button itself.
    this.addEventListener('shopify:block:select', this.#onBlockSelect);

    this.resizeObserver = new ResizeObserver(() => this.#positionPill());
    // The tablist, not the host: the pill tracks tab geometry, and with four tabs the row can
    // re-wrap (changing every tab's position) while the host's own width never changes.
    // requiredRefs deliberately omits `tablist`, hence the fallback.
    this.resizeObserver.observe(this.refs.tablist ?? this);
  }

  /**
   * The Section Rendering API re-renders this section on every theme-editor edit, and morph
   * (assets/morph.js) strips the inline styles and the --ready class the pill carries — leaving a
   * 0x0 pill parked at the origin until something else happened to move it. Re-deriving the whole
   * active state here restores panel visibility and the pill in one call.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#activate(this.#activeIndex);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener('shopify:block:select', this.#onBlockSelect);
    this.resizeObserver?.disconnect();
  }

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    const index = this.refs.tab?.indexOf(
      /** @type {HTMLButtonElement} */ (/** @type {HTMLElement} */ (event.target)?.closest('[role="tab"]'))
    );
    if (typeof index === 'number' && index > -1) this.#activate(index);
  };

  /**
   * @param {{ index: string | number }} data
   */
  selectPanel({ index }) {
    this.#activate(Number(index));
  }

  /**
   * Arrow-key traversal of the tablist, per the ARIA APG tabs pattern.
   *
   * Left/Right/Home/End only. ArrowUp/ArrowDown are deliberately NOT handled: role="tablist"
   * defaults to aria-orientation="horizontal", where Up/Down belong to the page, and swallowing
   * them would break scrolling for keyboard users on a page this section sits in the middle of.
   *
   * @param {KeyboardEvent} event
   */
  handleTabKeydown(event) {
    const { tab } = this.refs;
    if (!tab?.length) return;

    const current = tab.indexOf(/** @type {HTMLButtonElement} */ (event.target));
    if (current === -1) return;

    // Null-prototype so a key literally named "constructor" or "__proto__" can't resolve to an
    // inherited function and turn the clamp below into NaN.
    const offsets = Object.assign(Object.create(null), {
      ArrowLeft: -1,
      ArrowRight: 1,
      Home: -current,
      End: tab.length - 1 - current,
    });

    const delta = offsets[event.key];
    if (typeof delta !== 'number') return;

    event.preventDefault();
    const next = this.#activate(current + delta);
    if (next !== null) tab[next]?.focus();
  }

  /**
   * Single source of truth for "which tab is on". Clamps against BOTH arrays, so a tab/panel count
   * mismatch degrades to the last valid pair instead of hiding every panel and blanking the
   * section.
   *
   * @param {number} index
   * @returns {number | null} the index actually activated, or null if there was nothing to activate
   */
  #activate(index) {
    const { tab, panel } = this.refs;
    const count = Math.min(tab?.length ?? 0, panel?.length ?? 0);
    if (count === 0) return null;

    const target = Math.min(Math.max(Number.isInteger(index) ? index : 0, 0), count - 1);
    this.#activeIndex = target;

    tab.forEach((tabEl, i) => {
      tabEl.setAttribute('aria-selected', String(i === target));
      tabEl.tabIndex = i === target ? 0 : -1;
    });

    panel.forEach((panelEl, i) => {
      panelEl.hidden = i !== target;
    });

    this.#positionPill();
    return target;
  }

  #positionPill() {
    const { tab, pill } = this.refs;
    // Sourced from the DOM rather than #activeIndex: anything that rewrites this subtree (morph
    // after a theme-editor edit, a Section Rendering API swap) resets aria-selected without
    // telling the instance, and a stale field would park the pill under a tab that isn't the
    // selected one. The field is only the fallback.
    const selected = tab?.find((el) => el.getAttribute('aria-selected') === 'true');
    const activeTab = selected ?? tab?.[this.#activeIndex] ?? tab?.[0];
    if (!activeTab || !pill) return;

    const list = activeTab.parentElement;
    if (!list) return;

    const listRect = list.getBoundingClientRect();
    const { left, top, width, height } = activeTab.getBoundingClientRect();

    // top/height as well as left/width: with four tabs the row wraps to a grid on narrow
    // viewports, where the active tab can sit on the second line. A transform-only pill would
    // slide along the first row and land under the wrong tab.
    pill.style.width = `${width}px`;
    pill.style.height = `${height}px`;
    pill.style.transform = `translate(${left - listRect.left}px, ${top - listRect.top}px)`;

    // Suppresses the opening slide-from-origin on first paint: the pill only starts transitioning
    // once it has been placed. Read off the class rather than an instance field so a morph
    // re-render, which strips the class, correctly re-suppresses it.
    if (!pill.classList.contains('comparison-toggle__pill--ready')) {
      requestAnimationFrame(() => pill.classList.add('comparison-toggle__pill--ready'));
    }
  }
}

if (!customElements.get('comparison-tabs-component')) {
  customElements.define('comparison-tabs-component', ComparisonTabsComponent);
}
